import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const TOKEN_KEY = 'autocompare_token';

// Marks API calls made by the current build. nginx answers calls WITHOUT it (i.e. from an old copy of the
// app a browser kept from before the no-cache fix) with "Clear-Site-Data: cache", so that browser drops its
// stale pages and the next tab loads the current build. See frontend/nginx.conf.
const BUILD_HEADER = { 'X-App-Build': 'current' };

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json', ...BUILD_HEADER },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export const login = async (email, password) => {
  const body = new URLSearchParams();
  body.append('username', email);
  body.append('password', password);
  const { data } = await api.post('/auth/login', body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return data;
};

export const register = async ({ email, password, name, phone }) => {
  const { data } = await api.post('/auth/register', { email, password, name, phone });
  return data;
};

export const getMe = async () => {
  const { data } = await api.get('/auth/me');
  return data;
};

export const getVehicles = async (params = {}) => {
  const { data } = await api.get('/vehicles/', { params });
  return data;
};

export const compareVehicles = async (keyA, keyB) => {
  const { data } = await api.get(`/comparison/${keyA}/${keyB}`);
  return data;
};

export const calculateCosts = async (key) => {
  const { data } = await api.get(`/costs/${key}`);
  return data;
};

export const createInquiry = async (payload) => {
  const { data } = await api.post('/inquiries/', payload);
  return data;
};

export const createApplication = async (payload) => {
  const { data } = await api.post('/applications/', payload);
  return data;
};

export const validateDocument = async (fieldName, file) => {
  const formData = new FormData();
  formData.append('field_name', fieldName);
  formData.append('file', file);
  const { data } = await api.post('/applications/validate-document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const createServiceApplication = async (payload) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    formData.append(key, value);
  });
  const { data } = await api.post('/applications/service', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const fetchApplicationDocumentBlob = async (applicationId, documentId) => {
  const { data } = await api.get(`/applications/${applicationId}/documents/${documentId}`, {
    responseType: 'blob',
  });
  return data;
};

export const getOpenSlots = async (serviceType) => {
  const { data } = await api.get('/availability/open', { params: { service_type: serviceType } });
  return data;
};

export const getManagedSlots = async (serviceType) => {
  const { data } = await api.get('/availability/manage', { params: { service_type: serviceType } });
  return data;
};

export const saveAvailabilityDay = async (payload) => {
  const { data } = await api.put('/availability/day', payload);
  return data;
};

export const downloadApplicationDocument = async (applicationId, documentId, filename) => {
  const { data } = await api.get(`/applications/${applicationId}/documents/${documentId}`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || 'document';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

export const getDashboardStats = async () => {
  const { data } = await api.get('/dashboard/stats');
  return data;
};

export const getDashboardInquiries = async () => {
  const { data } = await api.get('/dashboard/inquiries');
  return data;
};

export const getDashboardApplications = async () => {
  const { data } = await api.get('/dashboard/applications');
  return data;
};

export const updateApplicationStatus = async (id, status) => {
  const { data } = await api.patch(`/applications/${id}/status`, { status });
  return data;
};

export const scheduleApplication = async (id, payload) => {
  const { data } = await api.post(`/applications/${id}/schedule`, payload);
  return data;
};

export const getApplicationScheduleHistory = async (id) => {
  const { data } = await api.get(`/applications/${id}/schedule-history`);
  return data;
};

export const updateInquiryStatus = async (id, status) => {
  const { data } = await api.patch(`/inquiries/${id}/status`, { status });
  return data;
};

// Admin: permanent delete (an application's documents and schedule history go with it)
export const deleteInquiry = async (id) => {
  const { data } = await api.delete(`/inquiries/${id}`);
  return data;
};

export const deleteApplication = async (id) => {
  const { data } = await api.delete(`/applications/${id}`);
  return data;
};


// CMS Brand Endpoints
export const getCmsBrands = async (search) => {
  const { data } = await api.get('/admin/cms/brands', { params: search ? { search } : {} });
  return data;
};

export const createCmsBrand = async (payload) => {
  const { data } = await api.post('/admin/cms/brands', payload);
  return data;
};

export const updateCmsBrand = async (brandId, payload) => {
  const { data } = await api.patch(`/admin/cms/brands/${brandId}`, payload);
  return data;
};

export const uploadBrandLogo = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post('/admin/cms/brands/upload-logo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

export const getCmsBrand = async (brandId) => {
  const { data } = await api.get(`/admin/cms/brands/${brandId}`);
  return data;
};

export const getCmsBrandVehicles = async (brandId) => {
  const { data } = await api.get(`/admin/cms/brands/${brandId}/vehicles`);
  return data;
};

export const createCmsVehicleUnderBrand = async (brandId, payload) => {
  const { data } = await api.post(`/admin/cms/brands/${brandId}/vehicles`, payload);
  return data;
};

// CMS Vehicle Endpoints
export const getCmsVehicles = async (params = {}) => {
  const { data } = await api.get('/admin/cms/vehicles', { params });
  return data;
};

export const getCmsVehicle = async (id) => {
  const { data } = await api.get(`/admin/cms/vehicles/${id}`);
  return data;
};

export const createCmsVehicle = async (payload) => {
  const { data } = await api.post('/admin/cms/vehicles', payload);
  return data;
};

export const updateCmsVehicle = async (id, payload) => {
  const { data } = await api.patch(`/admin/cms/vehicles/${id}`, payload);
  return data;
};

// Delete from the catalogue. The hub's Delete buttons call the same rules (backend/app/catalog_delete.py).
export const deleteCmsVehicle = async (id) => {
  const { data } = await api.delete(`/admin/cms/vehicles/${id}`);
  return data;
};

export const deleteCmsBrand = async (id) => {
  const { data } = await api.delete(`/admin/cms/brands/${id}`);
  return data;
};

export const uploadVehicleImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await api.post('/admin/cms/vehicles/upload-image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
};

// Public Brands Endpoint
export const getPublicBrands = async () => {
  const { data } = await api.get('/vehicles/brands');
  return data;
};

export default api;

